import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { money, formatDate, playerHistory } from "@/lib/scoring";
import { displayName } from "@/data/nicknames";

export const PlayerPanel = ({ name, rounds, onClose }) => {
  const history = playerHistory(rounds, name);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalWon = history.reduce((s, h) => s + h.totalWon, 0);
  const totalNet = history.reduce((s, h) => s + h.net, 0);
  const chartData = history.map((h) => ({
    date: h.date.slice(5).replace("-", "/"),
    won: +h.cumulative.toFixed(2),
  }));

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${name} round-by-round history`}
      data-testid="player-panel"
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-cypress-gold/30 bg-cypress-night p-5 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold tracking-tight" data-testid="player-panel-name">{displayName(name)}</h3>
            <p className="mt-0.5 font-mono text-xs text-cypress-cream/60">
              {history.length} rounds · won <span className="text-cypress-gold">{money(totalWon)}</span> ·{" "}
              {money(history.length ? totalWon / history.length : 0)} per round · net{" "}
              <span className={totalNet < 0 ? "text-red-400" : "text-emerald-400"}>{money(totalNet)}</span>
            </p>
          </div>
          <button
            type="button"
            data-testid="player-panel-close"
            onClick={onClose}
            aria-label="Close player history"
            className="rounded-full border border-cypress-gold/30 p-1.5 text-cypress-cream/70 hover:text-cypress-cream"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {chartData.length > 1 && (
          <div className="mt-4 rounded-lg border border-cypress-gold/15 bg-cypress-green/30 p-3" data-testid="player-panel-chart">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-cypress-cream/50">
              Cumulative winnings
            </p>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <XAxis dataKey="date" tick={{ fill: "#F0E6CE", opacity: 0.5, fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fill: "#F0E6CE", opacity: 0.5, fontSize: 10, fontFamily: "IBM Plex Mono" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `$${v}`}
                  width={52}
                />
                <Tooltip
                  contentStyle={{ background: "#0C1F14", border: "1px solid rgba(201,162,39,0.4)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#F0E6CE" }}
                  itemStyle={{ color: "#C9A227" }}
                  formatter={(v) => [money(v), "Cumulative"]}
                />
                <Line type="monotone" dataKey="won" stroke="#C9A227" strokeWidth={2} dot={{ r: 2.5, fill: "#C9A227" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-4 space-y-2" data-testid="player-panel-history">
          {[...history].reverse().map((h) => (
            <div
              key={h.date}
              className="flex items-center gap-3 rounded-lg border border-white/5 bg-cypress-green/30 px-3 py-2.5 text-sm"
              data-testid={`player-history-${h.date}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold tracking-tight">{formatDate(h.date)}</p>
                <p className="mt-0.5 font-mono text-xs text-cypress-cream/50">
                  {h.dots} dots · net <span className={h.net < 0 ? "text-red-400" : "text-emerald-400"}>{money(h.net)}</span>
                </p>
              </div>
              {h.parIn && (
                <span className="rounded-sm border border-cypress-gold/50 bg-cypress-gold/10 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-cypress-gold">
                  Par In
                </span>
              )}
              {h.leg && (
                <span className="rounded-sm border border-cypress-gold/50 bg-cypress-gold/10 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-cypress-gold">
                  {h.leg}
                </span>
              )}
              <span className="font-mono font-bold tabular-nums text-cypress-gold">{money(h.totalWon)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};
